import { Plugin, Widget } from 'ckeditor5'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { toWidget } from 'ckeditor5'
import { track } from '@amplitude/analytics-browser'
import CommunityBlock from '@minicoursegenerator/community-block'

const isPublicByAccessRule = accessRule => Number(accessRule) === 0

const resolvePublicCollectionState = ({ state, collectionId, course }) => {
  if (!state || !collectionId) {
    return { isPublicCollection: false, source: 'missing-state-or-collection' }
  }

  const normalizedCollectionId = String(collectionId)
  const currentCollection = state?.collection?.collection
  const currentCollectionSettings = state?.collection?.collectionSettings
  const collectionList = Array.isArray(state?.collection?.list)
    ? state.collection.list
    : []

  if (
    currentCollection?.id &&
    String(currentCollection.id) === normalizedCollectionId &&
    currentCollectionSettings?.accessRule !== undefined
  ) {
    return {
      isPublicCollection: isPublicByAccessRule(currentCollectionSettings.accessRule),
      source: 'collectionSettings',
    }
  }

  if (
    currentCollection?.id &&
    String(currentCollection.id) === normalizedCollectionId &&
    currentCollection?.accessRule !== undefined
  ) {
    return {
      isPublicCollection: isPublicByAccessRule(currentCollection.accessRule),
      source: 'collection',
    }
  }

  const matchingCollection = collectionList.find(
    collection => String(collection?.id) === normalizedCollectionId,
  )
  if (matchingCollection?.accessRule !== undefined) {
    return {
      isPublicCollection: isPublicByAccessRule(matchingCollection.accessRule),
      source: 'collectionList',
    }
  }

  if (
    course?.defaultCollectionDto?.id &&
    String(course.defaultCollectionDto.id) === normalizedCollectionId &&
    course.defaultCollectionDto?.accessRule !== undefined
  ) {
    return {
      isPublicCollection: isPublicByAccessRule(
        course.defaultCollectionDto.accessRule,
      ),
      source: 'course.defaultCollectionDto',
    }
  }

  if (course?.courseCollectionAccessRule !== undefined) {
    return {
      isPublicCollection: isPublicByAccessRule(course.courseCollectionAccessRule),
      source: 'course.courseCollectionAccessRule',
    }
  }

  if (course?.collectionAccessRule !== undefined) {
    return {
      isPublicCollection: isPublicByAccessRule(course.collectionAccessRule),
      source: 'course.collectionAccessRule',
    }
  }

  return { isPublicCollection: false, source: 'not-found' }
}

const trackCommunityPostCreated = payload => {
  try {
    track('community_post_created', {
      notify_learner:
        typeof payload?.notify_learner === 'boolean'
          ? payload.notify_learner
          : null,
      community_id: payload?.community_id ?? null,
      course_id: payload?.course_id ?? null,
      page_id: payload?.page_id ?? null,
    })
  } catch (error) {
    console.error(
      'Failed to track community_post_created amplitude event:',
      error,
    )
  }
}

export default class CommunityBlockPlugin extends Plugin {
  static get pluginName() {
    return 'CommunityBlockPlugin'
  }

  static get requires() {
    return [Widget]
  }

  constructor(editor) {
    super(editor)
    this.mountedRoots = new Map()
  }

  init() {
    const editor = this.editor

    editor.model.schema.register('communityBlock', {
      isObject: true,
      allowWhere: '$block',
      allowIn: 'column',
      allowAttributes: [
        'id',
        'data',
        'moderation',
        'visitorPermissions',
        'communityId',
        'communityLanguage',
      ],
    })

    this._defineConverters()

    // Community Block seçiliyken klavye ile silinmesini engelle
    editor.editing.view.document.on(
      'keydown',
      (evt, data) => {
        const selection = editor.model.document.selection
        const selectedElement = selection.getSelectedElement()

        if (selectedElement && selectedElement.name === 'communityBlock') {
          // Printable karakterler veya delete/backspace tuşları
          const domEvent = data.domEvent
          const key = domEvent.key

          // Allow: Escape, Arrow keys, Tab
          const allowedKeys = [
            'Escape',
            'ArrowUp',
            'ArrowDown',
            'ArrowLeft',
            'ArrowRight',
            'Tab',
          ]
          if (allowedKeys.includes(key)) {
            return
          }

          // Block: Delete, Backspace, and printable characters
          if (
            key === 'Delete' ||
            key === 'Backspace' ||
            (key.length === 1 && !domEvent.ctrlKey && !domEvent.metaKey)
          ) {
            evt.stop()
            data.preventDefault()
          }
        }
      },
      { priority: 'highest' },
    )

    editor.commands.add('insertCommunityBlock', {
      execute: () => {
        editor.model.change(writer => {
          // Community block için özel simpleBoxWrapper oluştur
          const simpleBoxWrapper = writer.createElement('simpleBoxWrapper', {
            layoutType: 'wide',
          })
          const simpleBox = writer.createElement('simpleBox')
          const column = writer.createElement('column')
          const communityBlock = writer.createElement('communityBlock', {})

          // Yapıyı oluştur
          writer.append(simpleBox, simpleBoxWrapper)
          writer.append(column, simpleBox)
          writer.append(communityBlock, column)

          // İçeriği ekle
          editor.model.insertContent(simpleBoxWrapper)
          writer.setSelection(communityBlock, 'on')
        })
      },
    })

    editor.ui.componentFactory.add('insertCommunityBlock', locale => {
      const button = document.createElement('button')
      button.className = 'ck-button ck-button_with-text'
      button.style.display = 'flex'
      button.style.alignItems = 'center'
      button.style.gap = '4px'
      button.style.padding = '8px 12px'

      const icon = document.createElement('span')
      icon.textContent = '👥'
      icon.style.fontSize = '16px'

      const text = document.createTextNode('Community')

      button.appendChild(icon)
      button.appendChild(text)

      button.onclick = () => {
        editor.execute('insertCommunityBlock')
      }

      return {
        element: button,
        render: () => { },
        destroy: () => { },
      }
    })

    // Listen for communityLanguage attribute changes and re-render React component
    editor.model.document.on('change:data', () => {
      const changes = editor.model.document.differ.getChanges()

      for (const change of changes) {
        if (
          change.type === 'attribute' &&
          change.attributeKey === 'communityLanguage' &&
          change.range
        ) {
          // Find the communityBlock element
          for (const item of change.range.getItems()) {
            if (item.name === 'communityBlock') {
              const viewElement = editor.editing.mapper.toViewElement(item)
              if (viewElement) {
                // Find the React placeholder DOM element
                const domElement = editor.editing.view.domConverter.mapViewToDom(
                  viewElement,
                )
                if (domElement) {
                  const placeholder = domElement.querySelector(
                    '.community-block-react-placeholder',
                  )
                  if (placeholder && this.mountedRoots.has(placeholder)) {
                    const root = this.mountedRoots.get(placeholder)
                    const newLanguage =
                      item.getAttribute('data-community-language') || 'en'

                    // Re-render with new language
                    this._rerenderCommunityBlock(root, placeholder, newLanguage)
                  }
                }
              }
            }
          }
        }
      }
    })
  }

  _rerenderCommunityBlock(root, domElement, newLanguage) {
    try {
      const urlPath = window.location.pathname
      const pageUrl = window.location.pathname.split('/')[1]
      const isMiniCourse = pageUrl === 'mini-course'
      const isCollection = pageUrl === 'collection'
      const isLandingPage = urlPath.includes('/landing-page')

      let courseId = null
      let collectionId = null
      let pageId = null
      let courseCode = null
      let courseName = null
      let collectionName = null
      let pageNumber = null
      let pageIndex = null
      let orderOfCard = null
      let userInfo = null
      let isPublicCollection = false

      if (isMiniCourse) {
        const courseIdMatch = urlPath.match(/\/mini-course\/(\d+)/)
        const pageNumberMatch = urlPath.match(/\/page\/(\d+)/)
        courseId =
          window.CONSTANTS?.COURSE_ID ||
          (courseIdMatch ? courseIdMatch[1] : null)
        pageId = pageNumberMatch ? pageNumberMatch[1] : null
      } else if (isCollection) {
        const collectionIdMatch = urlPath.match(/\/collection\/(\d+)/)
        collectionId = collectionIdMatch ? collectionIdMatch[1] : null
        if (isLandingPage) {
          pageId = 'landing-page'
        }
      }

      if (window.store && window.store.getState) {
        const state = window.store.getState()
        userInfo = state?.authentication?.me

        if (isMiniCourse) {
          const course = state?.course?.course
          courseCode = course?.code
          courseName = course?.name
          if (course?.courseCollectionId) {
            collectionId = String(course.courseCollectionId)
          }
          if (collectionId) {
            const publicCollectionResolution = resolvePublicCollectionState({
              state,
              collectionId,
              course,
            })
            isPublicCollection = publicCollectionResolution.isPublicCollection
          }
          const sections = course?.sections

          if (sections && Array.isArray(sections) && pageId) {
            for (let i = 0; i < sections.length; i++) {
              const section = sections[i]
              if (section.cards && Array.isArray(section.cards)) {
                const card = section.cards.find(card => card.id == pageId)
                if (card && card.orderOfCard !== undefined) {
                  pageIndex = i
                  orderOfCard = card.orderOfCard
                  pageNumber = card.orderOfCard + 1
                  break
                }
              }
            }
          }
        } else if (isCollection) {
          const collection = state?.collection?.collection
          courseCode = collection?.code
          collectionName = collection?.name
          const publicCollectionResolution = resolvePublicCollectionState({
            state,
            collectionId,
            course: null,
          })
          isPublicCollection = publicCollectionResolution.isPublicCollection
        }
      }

      if (!courseCode) {
        courseCode = window.CONSTANTS?.COURSE_CODE || window.CONSTANTS?.CODE
      }

      const domainName = 'share.minicoursegenerator.com'
      const shareLink = courseCode
        ? `https://${domainName}/${courseCode}`
        : null

      const parentContainer = domElement.closest(
        '.community-block-widget-container',
      )
      const communityId = parentContainer?.getAttribute('data-community-id')

      const derivedIsCategory = isCollection

      const props = {
        communityId: communityId,
        isAdmin: true,
        courseId: courseId,
        collectionId: collectionId,
        courseCode: courseCode,
        courseName: courseName,
        collectionName: collectionName,
        isCategory: derivedIsCategory,
        pageId: pageId,
        pageIndex: pageIndex,
        pageNumber: pageNumber,
        orderOfCard: orderOfCard,
        isLandingPage: isLandingPage,
        isMiniCourse: isMiniCourse,
        isCollection: isCollection,
        isPublicCollection: isPublicCollection,
        adminUrl: window.location.href,
        shareLink: shareLink,
        userInfo: userInfo,
        language: newLanguage,
        environment: process.env.REACT_APP_ENVIRONMENT || 'test',
        adminApiBaseUrl: process.env.REACT_APP_URL,
        enableNotifyLearners: true,
        onPostCreateSuccess: trackCommunityPostCreated,
      }

      console.log(
        '🔄 Re-rendering CommunityBlock with new language:',
        newLanguage,
        props,
      )
      root.render(React.createElement(CommunityBlock, props))
    } catch (error) {
      console.error('Error re-rendering CommunityBlock:', error)
    }
  }

  _defineConverters() {
    const editor = this.editor
    const conversion = editor.conversion

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        attributes: {
          'data-type': 'community-block',
        },
      },
      model: (viewElement, { writer }) => {
        const id = viewElement.getAttribute('id')
        const data = viewElement.getAttribute('data') || ''
        const moderation =
          viewElement.getAttribute('data-moderation') || 'aiAuto'
        const visitorPermissions =
          viewElement.getAttribute('data-visitor-permissions') || 'write'
        const communityId = viewElement.getAttribute('data-community-id')
        const communityLanguage =
          viewElement.getAttribute('data-community-language') || 'en'

        const attrs = {
          data,
          moderation,
          visitorPermissions,
          communityLanguage,
        }
        if (id) attrs.id = id
        if (communityId) attrs.communityId = communityId

        return writer.createElement('communityBlock', attrs)
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'communityBlock',
      view: (modelElement, { writer }) => {
        const id = modelElement.getAttribute('id')
        const data = modelElement.getAttribute('data') || ''
        const moderation = modelElement.getAttribute('moderation') || 'aiAuto'
        const visitorPermissions =
          modelElement.getAttribute('visitorPermissions') || 'write'
        const communityId = modelElement.getAttribute('communityId')
        const communityLanguage =
          modelElement.getAttribute('communityLanguage') || 'en'

        const attrs = {
          'data-type': 'community-block',
          data: data,
          'data-moderation': moderation,
          'data-visitor-permissions': visitorPermissions,
          'data-community-language': communityLanguage,
        }

        if (id !== undefined && id !== null) {
          attrs.id = id
        }

        if (communityId !== undefined && communityId !== null) {
          attrs['data-community-id'] = communityId
        }

        return writer.createEmptyElement('div', attrs)
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'communityBlock',
      view: (modelElement, { writer }) => {
        const moderation = modelElement.getAttribute('moderation') || 'aiAuto'
        const visitorPermissions =
          modelElement.getAttribute('visitorPermissions') || 'write'
        const communityId = modelElement.getAttribute('communityId')
        const communityLanguage =
          modelElement.getAttribute('communityLanguage') || 'en'

        const containerAttrs = {
          class: 'community-block-widget-container',
          'data-type': 'community-block',
          'data-moderation': moderation,
          'data-visitor-permissions': visitorPermissions,
          'data-community-language': communityLanguage,
        }

        if (communityId !== undefined && communityId !== null) {
          containerAttrs['data-community-id'] = communityId
        }

        const container = writer.createContainerElement('div', containerAttrs)

        const placeholder = writer.createRawElement(
          'div',
          {
            class: 'community-block-react-placeholder',
          },
          domElement => {
            // Event'lerin CKEditor'e ulaşmasını engelle - buton tıklamaları için gerekli
            domElement.addEventListener('mousedown', e => {
              e.stopPropagation()
            })
            domElement.addEventListener('click', e => {
              e.stopPropagation()
            })
            domElement.addEventListener('dblclick', e => {
              e.stopPropagation()
            })
            domElement.addEventListener('pointerdown', e => {
              e.stopPropagation()
            })
            domElement.addEventListener('pointermove', e => {
              e.stopPropagation()
            })
            domElement.addEventListener('pointerup', e => {
              e.stopPropagation()
            })
            domElement.addEventListener('keydown', e => {
              e.stopPropagation()
            })
            domElement.addEventListener('keyup', e => {
              e.stopPropagation()
            })
            domElement.addEventListener('input', e => {
              e.stopPropagation()
            })
            domElement.addEventListener(
              'focus',
              e => {
                e.stopPropagation()
              },
              true,
            )
            domElement.addEventListener(
              'blur',
              e => {
                e.stopPropagation()
              },
              true,
            )

            // Mount React component
            console.log('🔵 Community Block rendering started')
            try {
              // Use communityLanguage from closure (captured from modelElement above)
              // This is more reliable than getting it from DOM which may not be ready
              const langValue = communityLanguage // captured from closure (line 334-335)

              // URL'den courseId/collectionId ve pageId'yi parse et
              const urlPath = window.location.pathname
              const pageUrl = window.location.pathname.split('/')[1]

              // URL tipini belirle (mini-course veya collection)
              const isMiniCourse = pageUrl === 'mini-course'
              const isCollection = pageUrl === 'collection'
              const isLandingPage = urlPath.includes('/landing-page')

              let courseId = null
              let collectionId = null
              let pageId = null

              if (isMiniCourse) {
                const courseIdMatch = urlPath.match(/\/mini-course\/(\d+)/)
                const pageNumberMatch = urlPath.match(/\/page\/(\d+)/)
                courseId =
                  window.CONSTANTS?.COURSE_ID ||
                  (courseIdMatch ? courseIdMatch[1] : null)
                pageId = pageNumberMatch ? pageNumberMatch[1] : null
              } else if (isCollection) {
                const collectionIdMatch = urlPath.match(/\/collection\/(\d+)/)
                const pageMatch = urlPath.match(/\/landing-page/)
                collectionId = collectionIdMatch ? collectionIdMatch[1] : null
                if (isLandingPage) {
                  pageId = 'landing-page'
                }
              }

              console.log('🟣 URL Type:', {
                isMiniCourse,
                isCollection,
                isLandingPage,
              })

              // Redux store'dan course/collection bilgisini al
              let courseCode = null
              let courseName = null
              let collectionName = null
              let pageNumber = null
              let pageIndex = null
              let orderOfCard = null
              let sections = null
              let userInfo = null
              let isPublicCollection = false

              try {
                if (window.store && window.store.getState) {
                  const state = window.store.getState()

                  // User bilgisini al
                  userInfo = state?.authentication?.me
                  console.log('🔵 Redux state.authentication.me:', userInfo)

                  if (isMiniCourse) {
                    const course = state?.course?.course

                    console.log('🔵 Redux state.course.course:', course)
                    console.log('🔵 Course sections:', course?.sections)

                    courseCode = course?.code
                    courseName = course?.name
                    if (course?.courseCollectionId) {
                      collectionId = String(course.courseCollectionId)
                    }
                    if (collectionId) {
                      const publicCollectionResolution =
                        resolvePublicCollectionState({
                          state,
                          collectionId,
                          course,
                        })
                      isPublicCollection =
                        publicCollectionResolution.isPublicCollection
                      console.log(
                        '🔵 Public learning path resolution:',
                        publicCollectionResolution,
                      )
                    }
                    sections = course?.sections
                  } else if (isCollection) {
                    const collection = state?.collection?.collection

                    console.log(
                      '🔵 Redux state.collection.collection:',
                      collection,
                    )
                    console.log('🔵 Collection courses:', collection?.courses)

                    courseCode = collection?.code
                    collectionName = collection?.name
                    const publicCollectionResolution =
                      resolvePublicCollectionState({
                        state,
                        collectionId,
                        course: null,
                      })
                    isPublicCollection =
                      publicCollectionResolution.isPublicCollection
                    console.log(
                      '🔵 Public learning path resolution:',
                      publicCollectionResolution,
                    )
                  }

                  // Card'ın orderOfCard değerini ve section index'ini bul (sadece mini-course için)
                  if (
                    isMiniCourse &&
                    sections &&
                    Array.isArray(sections) &&
                    pageId
                  ) {
                    for (let i = 0; i < sections.length; i++) {
                      const section = sections[i]
                      if (section.cards && Array.isArray(section.cards)) {
                        const card = section.cards.find(
                          card => card.id == pageId,
                        )
                        if (card && card.orderOfCard !== undefined) {
                          pageIndex = i
                          orderOfCard = card.orderOfCard
                          pageNumber = card.orderOfCard + 1
                          console.log('🔵 Found card:', card)
                          console.log(
                            '🔵 Section index (pageIndex):',
                            pageIndex,
                          )
                          console.log('🔵 orderOfCard:', orderOfCard)
                          console.log(
                            '🔵 pageNumber (orderOfCard + 1):',
                            pageNumber,
                          )
                          break
                        }
                      }
                    }

                    if (pageNumber === null) {
                      console.log('🔴 Card bulunamadı veya orderOfCard yok')
                    }
                  }
                } else {
                  console.log('🔴 window.store bulunamadı!')
                }
              } catch (e) {
                console.log('🔴 Redux store error:', e)
              }

              // Fallback: window.CONSTANTS'tan dene
              if (!courseCode) {
                courseCode =
                  window.CONSTANTS?.COURSE_CODE || window.CONSTANTS?.CODE
              }

              const domainName = 'share.minicoursegenerator.com'
              const shareLink = courseCode
                ? `https://${domainName}/${courseCode}`
                : null

              console.log('🟢 window.CONSTANTS:', window.CONSTANTS)
              console.log('🟢 Parsed courseId:', courseId)
              console.log('🟢 Parsed collectionId:', collectionId)
              console.log('🟢 Parsed pageId:', pageId)
              console.log('🟢 pageIndex (section index):', pageIndex)
              console.log('🟢 orderOfCard:', orderOfCard)
              console.log('🟢 pageNumber (orderOfCard + 1):', pageNumber)
              console.log('🟢 courseCode:', courseCode)
              console.log('🟢 courseName:', courseName)
              console.log('🟢 collectionName:', collectionName)
              console.log('🟢 isLandingPage:', isLandingPage)
              console.log('🟢 Share link:', shareLink)
              console.log('🟢 communityLanguage:', communityLanguage)

              const derivedIsCategory = isCollection

              const props = {
                communityId: communityId,
                isAdmin: true,
                courseId: courseId,
                collectionId: collectionId,
                courseCode: courseCode,
                courseName: courseName,
                collectionName: collectionName,
                isCategory: derivedIsCategory,
                pageId: pageId,
                pageIndex: pageIndex,
                pageNumber: pageNumber,
                orderOfCard: orderOfCard,
                isLandingPage: isLandingPage,
                isMiniCourse: isMiniCourse,
                isCollection: isCollection,
                isPublicCollection: isPublicCollection,
                adminUrl: window.location.href,
                shareLink: shareLink,
                userInfo: userInfo,
                language: communityLanguage,
                environment: process.env.REACT_APP_ENVIRONMENT || 'test',
                adminApiBaseUrl: process.env.REACT_APP_URL,
                enableNotifyLearners: true,
                onPostCreateSuccess: trackCommunityPostCreated,
              }

              console.log('🟡 CommunityBlock Props:', props)

              const root = createRoot(domElement)
              root.render(React.createElement(CommunityBlock, props))

              editor.plugins
                .get('CommunityBlockPlugin')
                .mountedRoots.set(domElement, root)
            } catch (error) {
              console.error('Error rendering CommunityBlock component:', error)
              domElement.innerHTML = `
                <div style="padding: 20px; background: #f5f5f5; border-radius: 8px; text-align: center;">
                  <span style="font-size: 24px;">👥</span>
                  <p>Community Board</p>
                  <small>Component not available</small>
                </div>
              `
            }
          },
        )

        writer.insert(writer.createPositionAt(container, 0), placeholder)

        return toWidget(container, writer, {
          label: 'Community Board',
          hasSelectionHandle: true,
        })
      },
    })
  }

  destroy() {
    for (const [domElement, root] of this.mountedRoots) {
      try {
        root.unmount()
      } catch (error) {
        console.error('Error unmounting React component:', error)
      }
    }
    this.mountedRoots.clear()
    super.destroy()
  }
}

export { CommunityBlockPlugin }
